/**
 * Language selector component.
 * @module components/LanguageSelector/LanguageSelector
 *
 * Added volto-google-translate list from siteLanguages
 */

import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';

import cx from 'classnames';
import { find, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { flattenToAppURL, Helmet, langmap } from '@plone/volto/helpers';

import config from '@plone/volto/registry';

import { defineMessages, useIntl } from 'react-intl';

import { changeLanguage, reset } from 'volto-google-translate/actions';
import {
  translateIsEnabled,
  useIsReadyToTranslate,
} from 'volto-google-translate/helpers';

const messages = defineMessages({
  switchLanguageTo: {
    id: 'Switch to',
    defaultMessage: 'Switch to',
  },
  language: {
    id: 'Language',
    defaultMessage: 'Language',
  },
});

const LanguageSelector = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentLang = useSelector((state) => state.intl.locale);
  const translateLanguage = useSelector(
    (state) => state.googleTranslate?.language,
  );
  const readyToTranslate = useIsReadyToTranslate();

  const defaultLanguage = useSelector((state) => {
    // Volto 18 `@@site` endpoint support
    if (state.site) {
      return state.site.data?.['plone.default_language'] || '';
    }
    // cust.restapi.info/ volto-siteinfo support
    if (state.siteInfo) {
      return state.siteInfo.default_language || '';
    }
    return '';
  });
  const pageLanguage = useSelector(
    (state) => state.content?.data?.language?.token,
  );
  const siteLanguages = useSelector((state) => {
    let languages = [];
    // cust.restapi.info/ volto-siteinfo support
    if (state.siteInfo?.available_languages) {
      languages = state.siteInfo.available_languages;
    }
    // Volto 18 `@@site` endpoint support
    if (state.site?.data?.['plone.available_languages']) {
      languages = state.site.data['plone.available_languages'];
    }
    languages = languages.filter(
      (language) => language !== defaultLanguage && language !== pageLanguage,
    );
    return languages;
  });
  const translations = useSelector(
    (state) => state.content.data?.['@components']?.translations?.items,
  );

  const { settings } = config;

  if (settings.isMultilingual) {
    return (
      <div className="language-selector notranslate">
        {map(settings.supportedLanguages, (lang) => {
          const translation = find(translations, { language: lang });
          return (
            <Link
              aria-label={`${intl.formatMessage(
                messages.switchLanguageTo,
              )} ${langmap[lang].nativeName.toLowerCase()}`}
              className={cx({ selected: lang === currentLang })}
              to={
                translation ? flattenToAppURL(translation['@id']) : `/${lang}`
              }
              title={langmap[lang].nativeName}
              onClick={() => {
                props.onClickAction();
              }}
              key={`language-selector-${lang}`}
            >
              {langmap[lang].nativeName}
            </Link>
          );
        })}
      </div>
    );
  }

  if (translateIsEnabled() && siteLanguages) {
    const SelectWidget = config.widgets.widget.select;
    const LanguageSelectorComponent =
      config.getComponent({ name: 'VoltoGoogleTranslateSelect' })?.component ||
      SelectWidget;

    const languageChoices = siteLanguages
      .reduce((choices, language) => {
        const mappedLanguage = langmap[language];
        if (mappedLanguage) {
          const label =
            mappedLanguage.englishName === mappedLanguage.nativeName
              ? mappedLanguage.englishName
              : `${mappedLanguage.englishName} (${mappedLanguage.nativeName})`;
          choices.push([language, label]);
        } else {
          // console.error('Unknown language for language selector: ', language);
        }
        return choices;
      }, [])
      .sort((a, b) => a[1].localeCompare(b[1]));

    return (
      <div
        id="google-translate-wrapper"
        className="notranslate"
        style={{ visibility: readyToTranslate ? 'visible' : 'hidden' }}
      >
        <LanguageSelectorComponent
          id="google-translate-language-select"
          choices={languageChoices}
          options={languageChoices} // choices and options are used by various components, map both just incase.
          noValueOption={
            langmap[pageLanguage || defaultLanguage]?.nativeName ||
            intl.formatMessage(messages.language)
          }
          value={translateLanguage}
          onChange={(event) => {
            const value =
              event.target.value || event.target.dataset.language || '';
            if (value === defaultLanguage || value === '') {
              dispatch(reset());
            } else {
              dispatch(changeLanguage(value));
            }
          }}
        />
      </div>
    );
  }

  return (
    <Helmet>
      <html lang={settings.defaultLanguage} />
    </Helmet>
  );
};

LanguageSelector.propTypes = {
  onClickAction: PropTypes.func,
};

LanguageSelector.defaultProps = {
  onClickAction: () => {},
};

export default LanguageSelector;
